/**
 * CircleIconButton — reusable circular button with icon.
 * Replaces the duplicated FocusButton and IconButton patterns.
 *
 * Props:
 *   size       — diameter in px (default 60)
 *   iconSize   — icon width/height (default size * 0.53)
 *   src        — icon image URL
 *   focusColor — border color on focus (default "#326dcf")
 *   bgColor    — inner circle background (default "#26272c")
 *   x, y       — position
 *   autofocus  — auto-focus on mount
 *   ref        — element ref callback
 *   onEnter, onLeft, onRight, onUp, onDown — navigation handlers
 */

import { hexColor, View } from "@lightningtv/solid";
import { createSignal, Show, type JSX } from "solid-js";

export interface CircleIconButtonProps {
  size?: number;
  iconSize?: number;
  src: string;
  focusColor?: string;
  bgColor?: string;
  x?: number;
  y?: number;
  autofocus?: boolean;
  ref?: (el: any) => void;
  onEnter?: () => void;
  onLeft?: () => void;
  onRight?: () => void;
  onUp?: () => void;
  onDown?: () => void;
}

const CircleIconButton = (props: CircleIconButtonProps) => {
  const [focused, setFocused] = createSignal(false);

  const size = () => props.size ?? 60;
  const iconSz = () => props.iconSize ?? Math.round(size() * 0.53);
  const borderSize = () => size() + 4;
  const iconOffset = () => Math.round((size() - iconSz()) / 2);

  return (
    <View
      ref={(el: any) => props.ref?.(el)}
      x={props.x}
      y={props.y}
      width={size()}
      height={size()}
      borderRadius={size() / 2}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onEnter={props.onEnter}
      onLeft={props.onLeft}
      onRight={props.onRight}
      onUp={props.onUp}
      onDown={props.onDown}
      autofocus={props.autofocus}
    >
      {/* Focus ring */}
      <Show when={focused()}>
        <View
          x={-2}
          y={-2}
          width={borderSize()}
          height={borderSize()}
          borderRadius={borderSize() / 2}
          color={hexColor(props.focusColor ?? "#326dcf")}
          zIndex={-1}
        />
      </Show>

      {/* Inner circle + icon */}
      <View
        x={0}
        y={0}
        width={size()}
        height={size()}
        borderRadius={size() / 2}
        color={hexColor(props.bgColor ?? "#26272c")}
      >
        <View
          x={iconOffset()}
          y={iconOffset()}
          src={props.src}
          width={iconSz()}
          height={iconSz()}
        />
      </View>
    </View>
  );
};

export default CircleIconButton;
